import React, { useEffect, useState } from "react";
import { supabase, CLINIC_ID } from "./supabase.js";
import { friendlyError, rupiah } from "./lib/format.js";

const divisionOptions = [
  { value: "ALL", label: "Semua divisi" },
  { value: "WELLNESS", label: "Wellness / TCM" },
  { value: "ESTETIK", label: "Estetik" },
  { value: "DENTAL", label: "Dental" },
  { value: "DOKTER_UMUM", label: "Dokter Umum" },
  { value: "FARMASI", label: "Farmasi" },
  { value: "FISIOTERAPI", label: "Fisioterapi" },
  { value: "SALON", label: "Salon" },
];

const emptyForm = {
  name: "",
  price: "",
  commission: "",
  duration: "",
  status: "ACTIVE",
  division: "ALL",
};

const emptyCostForm = {
  name: "",
  cost_type: "PER_TREATMENT",
  amount: "",
  notes: "",
};

const COST_TYPE_OPTIONS = [
  { value: "PER_TREATMENT", label: "Per tindakan" },
  { value: "PERCENTAGE", label: "Persentase tarif" },
  { value: "ALLOCATED", label: "Alokasi operasional" },
  { value: "MANUAL", label: "Manual" },
];

const emptyAutoBhpForm = {
  bhp_item_id: "",
  quantity: "1",
  notes: "",
};

const divisionLabels = {
  ...Object.fromEntries(divisionOptions.map((item) => [item.value, item.label])),
};

function serviceDivisionLabel(division) {
  const key = String(division || "ALL").toUpperCase();
  return divisionLabels[key] || key;
}

export function MasterTindakanPage() {
  const [rows, setRows] = useState([]);
  const [costRows, setCostRows] = useState([]);
  const [costCatalogRows, setCostCatalogRows] = useState([]);
  const [bhpRows, setBhpRows] = useState([]);
  const [autoBhpRows, setAutoBhpRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [costForm, setCostForm] = useState(emptyCostForm);
  const [autoBhpForm, setAutoBhpForm] = useState(emptyAutoBhpForm);
  const [selectedCostCatalogId, setSelectedCostCatalogId] = useState("");
  const [divisionFilter, setDivisionFilter] = useState("ALL");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingCost, setSavingCost] = useState(false);
  const [savingAutoBhp, setSavingAutoBhp] = useState(false);
  const [costEditMode, setCostEditMode] = useState(false);
  const [costEditValues, setCostEditValues] = useState({});
  const [savingAllCosts, setSavingAllCosts] = useState(false);

  async function fetchServices() {
    setLoading(true);
    setError("");
    try {
      const [servicesResult, costsResult, costCatalogResult, bhpResult, autoBhpResult] = await Promise.all([
        supabase
          .from("services")
          .select("*")
          .eq("clinic_id", CLINIC_ID)
          .order("name"),
        supabase
          .from("service_cost_components")
          .select("*")
          .eq("clinic_id", CLINIC_ID)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: true }),
        supabase
          .from("service_cost_catalog")
          .select("*")
          .eq("clinic_id", CLINIC_ID)
          .eq("status", "ACTIVE")
          .order("sort_order", { ascending: true })
          .order("name"),
        supabase
          .from("bhp_items")
          .select("id,name,unit,division,status")
          .eq("clinic_id", CLINIC_ID)
          .order("name"),
        supabase
          .from("service_bhp_defaults")
          .select("*")
          .eq("clinic_id", CLINIC_ID)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: true }),
      ]);
      if (servicesResult.error) throw servicesResult.error;
      if (costsResult.error) throw costsResult.error;
      if (costCatalogResult.error) throw costCatalogResult.error;
      if (bhpResult.error) throw bhpResult.error;
      if (autoBhpResult.error) throw autoBhpResult.error;
      setRows(servicesResult.data || []);
      setCostRows(costsResult.data || []);
      setCostCatalogRows(costCatalogResult.data || []);
      setBhpRows(bhpResult.data || []);
      setAutoBhpRows(autoBhpResult.data || []);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchServices();
  }, []);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateCostField(key, value) {
    setCostForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSelectCostCatalog(catalogId) {
    setSelectedCostCatalogId(catalogId);
    const item = costCatalogRows.find((row) => row.id === catalogId);
    if (!item) return;
    setCostForm({
      name: item.name || "",
      cost_type: item.cost_type || "PER_TREATMENT",
      amount: String(item.amount ?? ""),
      notes: item.notes || "",
    });
  }

  function updateAutoBhpField(key, value) {
    setAutoBhpForm((prev) => ({ ...prev, [key]: value }));
  }

  function resetForm() {
    setForm(emptyForm);
    setCostForm(emptyCostForm);
    setAutoBhpForm(emptyAutoBhpForm);
    setSelectedCostCatalogId("");
    setEditingId(null);
  }

  function startEdit(row) {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      price: row.price ?? "",
      commission: row.commission ?? "",
      duration: row.duration ?? "",
      status: row.status || "ACTIVE",
      division: row.division || "ALL",
    });
    setCostForm(emptyCostForm);
    setAutoBhpForm(emptyAutoBhpForm);
    setSelectedCostCatalogId("");
  }

  function componentCostAmount(cost, service) {
    if (cost.cost_type === "PERCENTAGE") {
      return (Number(service?.price || 0) * Number(cost.amount || 0)) / 100;
    }
    return Number(cost.amount || 0);
  }

  function formatCostAmount(cost) {
    if (cost.cost_type === "PERCENTAGE") {
      return `${Number(cost.amount || 0)}%`;
    }
    return rupiah.format(Number(cost.amount || 0));
  }

  function serviceCosts(serviceId) {
    return costRows.filter((item) => item.service_id === serviceId);
  }

  function serviceAutoBhp(serviceId) {
    return autoBhpRows.filter((item) => item.service_id === serviceId);
  }

  function bhpItemLabel(id) {
    const item = bhpRows.find((row) => row.id === id);
    if (!item) return id || "-";
    return `${item.name}${item.unit ? ` (${item.unit})` : ""}`;
  }

  function selectedBhpItem(id) {
    return bhpRows.find((row) => row.id === id) || null;
  }

  function isDiscreteUnit(unit) {
    return /^(pcs|pc|buah|pasang|lembar|sheet|box|kotak|botol|ampul|sachet|strip|unit)$/i.test(String(unit || "").trim());
  }

  function quantityStepForBhp(id) {
    return isDiscreteUnit(selectedBhpItem(id)?.unit) ? "1" : "0.01";
  }

  function normalizeBhpQuantity(value, id) {
    const quantity = Number(value);
    if (!Number.isFinite(quantity) || quantity <= 0) return "1";
    if (isDiscreteUnit(selectedBhpItem(id)?.unit)) return String(Math.max(1, Math.round(quantity)));
    return String(quantity);
  }

  function compatibleBhpItems(service) {
    const serviceDivision = (service?.division || form.division || "ALL").toUpperCase();
    const activeRows = bhpRows.filter((row) => !/inactive|nonaktif/i.test(row.status || ""));
    if (serviceDivision === "ALL") return activeRows;
    return activeRows.filter((row) => {
      const bhpDivision = (row.division || "ALL").toUpperCase();
      return bhpDivision === "ALL" || bhpDivision === serviceDivision;
    });
  }

  function serviceCostTotal(serviceId) {
    const service = rows.find((row) => row.id === serviceId);
    return serviceCosts(serviceId).reduce((sum, item) => sum + componentCostAmount(item, service), 0);
  }

  const editingCosts = editingId ? serviceCosts(editingId) : [];
  const editingAutoBhp = editingId ? serviceAutoBhp(editingId) : [];
  const visibleRows = rows.filter((row) => {
    if (divisionFilter === "ALL") return true;
    return String(row.division || "ALL").toUpperCase() === divisionFilter;
  });
  const visibleDivisionTabs = divisionOptions.filter((option) => {
    if (option.value === "ALL") return true;
    return rows.some((row) => String(row.division || "ALL").toUpperCase() === option.value);
  });
  const serviceCountByDivision = rows.reduce((acc, row) => {
    const key = String(row.division || "ALL").toUpperCase();
    acc[key] = (acc[key] || 0) + 1;
    acc.ALL = (acc.ALL || 0) + 1;
    return acc;
  }, { ALL: 0 });

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Nama tindakan wajib diisi.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        clinic_id: CLINIC_ID,
        name: form.name.trim(),
        price: Number(form.price) || 0,
        commission: Number(form.commission) || 0,
        duration: Number(form.duration) || 0,
        status: form.status,
        division: form.division || 'ALL',
      };
      if (editingId) {
        const { error: updateError } = await supabase
          .from("services")
          .update(payload)
          .eq("id", editingId);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from("services")
          .insert(payload);
        if (insertError) throw insertError;
      }
      resetForm();
      await fetchServices();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    if (!window.confirm(`Hapus tindakan "${row.name}"?`)) return;
    setError("");
    try {
      const { error: deleteError } = await supabase
        .from("services")
        .delete()
        .eq("id", row.id);
      if (deleteError) throw deleteError;
      if (editingId === row.id) resetForm();
      await fetchServices();
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  async function handleAddCost(e) {
    e.preventDefault();
    if (!editingId) return;
    if (!costForm.name.trim()) {
      setError("Nama pos biaya wajib diisi.");
      return;
    }
    setSavingCost(true);
    setError("");
    try {
      const { error: insertError } = await supabase
        .from("service_cost_components")
        .insert({
          clinic_id: CLINIC_ID,
          service_id: editingId,
          name: costForm.name.trim(),
          cost_type: costForm.cost_type || "PER_TREATMENT",
          amount: Number(costForm.amount) || 0,
          notes: costForm.notes.trim() || null,
          sort_order: editingCosts.length + 1,
        });
      if (insertError) throw insertError;
      setCostForm(emptyCostForm);
      setSelectedCostCatalogId("");
      await fetchServices();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSavingCost(false);
    }
  }

  async function handleDeleteCost(cost) {
    if (!window.confirm(`Hapus pos biaya "${cost.name}"?`)) return;
    setError("");
    try {
      const { error: deleteError } = await supabase
        .from("service_cost_components")
        .delete()
        .eq("id", cost.id);
      if (deleteError) throw deleteError;
      await fetchServices();
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  async function handleAddAutoBhp(e) {
    e.preventDefault();
    if (!editingId) return;
    if (!autoBhpForm.bhp_item_id) {
      setError("Item BHP wajib dipilih.");
      return;
    }
    const quantity = Number(normalizeBhpQuantity(autoBhpForm.quantity, autoBhpForm.bhp_item_id));
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setError("Jumlah BHP otomatis harus lebih dari nol.");
      return;
    }
    if (editingAutoBhp.some((row) => row.bhp_item_id === autoBhpForm.bhp_item_id)) {
      setError("BHP otomatis yang sama tidak boleh diulang untuk tindakan ini.");
      return;
    }
    setSavingAutoBhp(true);
    setError("");
    try {
      const { error: insertError } = await supabase
        .from("service_bhp_defaults")
        .insert({
          clinic_id: CLINIC_ID,
          service_id: editingId,
          bhp_item_id: autoBhpForm.bhp_item_id,
          quantity,
          notes: autoBhpForm.notes.trim() || null,
          sort_order: editingAutoBhp.length + 1,
        });
      if (insertError) throw insertError;
      setAutoBhpForm(emptyAutoBhpForm);
      await fetchServices();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSavingAutoBhp(false);
    }
  }

  function startCostEditMode(costs) {
    const initial = {};
    costs.forEach((c) => {
      initial[c.id] = { cost_type: c.cost_type || "PER_TREATMENT", amount: String(c.amount ?? ""), notes: c.notes || "" };
    });
    setCostEditValues(initial);
    setCostEditMode(true);
  }

  function cancelCostEditMode() {
    setCostEditMode(false);
    setCostEditValues({});
  }

  function updateCostEditValue(id, key, value) {
    setCostEditValues((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
  }

  async function handleSaveAllCosts(costs) {
    setSavingAllCosts(true);
    setError("");
    try {
      const results = await Promise.all(
        costs.map((cost) => {
          const val = costEditValues[cost.id];
          if (!val) return Promise.resolve();
          return supabase
            .from("service_cost_components")
            .update({ cost_type: val.cost_type || "PER_TREATMENT", amount: Number(val.amount) || 0, notes: val.notes.trim() || null })
            .eq("id", cost.id);
        })
      );
      const failed = results.find((result) => result?.error);
      if (failed?.error) throw failed.error;
      cancelCostEditMode();
      await fetchServices();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSavingAllCosts(false);
    }
  }

  async function handleDeleteAutoBhp(row) {
    if (!window.confirm(`Hapus BHP otomatis "${bhpItemLabel(row.bhp_item_id)}"?`)) return;
    setError("");
    try {
      const { error: deleteError } = await supabase
        .from("service_bhp_defaults")
        .delete()
        .eq("id", row.id);
      if (deleteError) throw deleteError;
      await fetchServices();
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  return (
    <div className="page-stack">
      {/* ── Panel Tambah Tindakan Baru ── */}
      <article className="panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">Master Data XV</span>
            <h3>Tambah Tindakan Baru</h3>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            <strong>Terjadi kesalahan.</strong> {error}
          </div>
        )}

        <form className="mt-form" onSubmit={handleSubmit}>
          <div className="mt-form-row">
            <div className="mt-field">
              <label>Nama Tindakan</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                disabled={!!editingId}
              />
            </div>
            <div className="mt-field">
              <label>Tarif</label>
              <input
                type="number"
                value={form.price}
                onChange={(e) => updateField("price", e.target.value)}
                disabled={!!editingId}
              />
            </div>
            <div className="mt-field">
              <label>Komisi Nakes</label>
              <input
                type="number"
                value={form.commission}
                onChange={(e) => updateField("commission", e.target.value)}
                disabled={!!editingId}
              />
            </div>
          </div>
          <div className="mt-form-row">
            <div className="mt-field">
              <label>Durasi Menit</label>
              <input
                type="number"
                value={form.duration}
                onChange={(e) => updateField("duration", e.target.value)}
                disabled={!!editingId}
              />
            </div>
            <div className="mt-field">
              <label>Divisi</label>
              <select
                value={form.division}
                onChange={(e) => updateField("division", e.target.value)}
                disabled={!!editingId}
              >
                {divisionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-field">
              <label>Status</label>
              <select
                value={form.status}
                onChange={(e) => updateField("status", e.target.value)}
                disabled={!!editingId}
              >
                <option value="ACTIVE">Aktif</option>
                <option value="INACTIVE">Nonaktif</option>
              </select>
            </div>
            <div className="mt-field mt-field-actions">
              {editingId ? (
                <div className="edit-active-notice">
                  <span>✏ Mengedit tindakan di tabel bawah</span>
                  <button type="button" className="button button-quiet" onClick={resetForm}>
                    Batal Edit
                  </button>
                </div>
              ) : (
                <button
                  type="submit"
                  className="button button-primary"
                  disabled={saving}
                >
                  {saving ? "Menyimpan…" : "Simpan Tindakan"}
                </button>
              )}
            </div>
          </div>
        </form>
      </article>

      {/* ── Panel Daftar Tindakan ── */}
      <article className="panel table-panel">
        <div className="panel-head" style={{ padding: "1.25rem 1.25rem 0" }}>
          <div>
            <span className="eyebrow">Layanan Klinik</span>
            <h3>Daftar Tindakan per Poli</h3>
          </div>
          <span className="status status-neutral">{visibleRows.length} tindakan</span>
        </div>
        <div className="service-tabs" role="tablist" aria-label="Filter layanan per poli">
          {visibleDivisionTabs.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`service-tab ${divisionFilter === option.value ? "service-tab-active" : ""}`}
              onClick={() => setDivisionFilter(option.value)}
            >
              {option.label}
              <span>{serviceCountByDivision[option.value] || 0}</span>
            </button>
          ))}
        </div>
        {loading ? (
          <div className="content-loading">
            <div className="loader" />
            <span>Memuat data tindakan…</span>
          </div>
        ) : (
          <table className="mt-table">
            <thead>
              <tr>
                <th>Tindakan</th>
                <th>Divisi</th>
                <th>Tarif</th>
                <th>Komisi</th>
                <th>Biaya</th>
                <th>BHP Auto</th>
                <th>Est. Profit</th>
                <th>Durasi</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const isEditing = editingId === row.id;
                const rowCosts = serviceCosts(row.id);
                const rowAutoBhp = serviceAutoBhp(row.id);
                const rowCostTotal = serviceCostTotal(row.id);
                const rowProfit = Number(row.price || 0) - Number(row.commission || 0) - rowCostTotal;
                const rowCompatibleBhp = compatibleBhpItems(row);
                const rowDivision = String(row.division || "ALL").toUpperCase();
                const rowCompatibleCostCatalog = costCatalogRows.filter((c) => {
                  const cd = String(c.division || "ALL").toUpperCase();
                  return rowDivision === "ALL" || cd === "ALL" || cd === rowDivision;
                });
                return (
                  <React.Fragment key={row.id}>
                    <tr className={isEditing ? "mt-row-editing" : ""}>
                      <td>{row.name}</td>
                      <td><span className={`status status-${row.division === 'ESTETIK' ? 'info' : row.division === 'DENTAL' ? 'warning' : row.division === 'WELLNESS' ? 'positive' : 'neutral'}`}>{row.division || 'ALL'}</span></td>
                      <td>{rupiah.format(Number(row.price || 0))}</td>
                      <td>{rupiah.format(Number(row.commission || 0))}</td>
                      <td>{rupiah.format(rowCostTotal)}</td>
                      <td>{rowAutoBhp.length} item</td>
                      <td>{rupiah.format(rowProfit)}</td>
                      <td>{row.duration || 0} menit</td>
                      <td>
                        <span className={`status status-${/nonaktif/i.test(row.status || "") ? "danger" : "positive"}`}>
                          {row.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className={`link-button${isEditing ? " link-active" : ""}`}
                          onClick={() => isEditing ? resetForm() : startEdit(row)}
                        >
                          {isEditing ? "Tutup ▲" : "Edit"}
                        </button>{" "}
                        <button
                          className="link-button link-danger"
                          onClick={() => handleDelete(row)}
                        >
                          Hapus
                        </button>
                      </td>
                    </tr>

                    {/* ── Inline Edit Expansion ── */}
                    {isEditing && (
                      <tr className="edit-expansion-row">
                        <td colSpan={10} className="edit-expansion-cell">
                          <div className="edit-expansion-inner">

                            {/* Form edit data dasar */}
                            <form className="mt-form" onSubmit={handleSubmit}>
                              <div className="expansion-form-header">
                                <strong>✏ Edit: {row.name}</strong>
                              </div>
                              <div className="mt-form-row">
                                <div className="mt-field">
                                  <label>Nama Tindakan</label>
                                  <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => updateField("name", e.target.value)}
                                  />
                                </div>
                                <div className="mt-field">
                                  <label>Tarif</label>
                                  <input
                                    type="number"
                                    value={form.price}
                                    onChange={(e) => updateField("price", e.target.value)}
                                  />
                                </div>
                                <div className="mt-field">
                                  <label>Komisi Nakes</label>
                                  <input
                                    type="number"
                                    value={form.commission}
                                    onChange={(e) => updateField("commission", e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="mt-form-row">
                                <div className="mt-field">
                                  <label>Durasi Menit</label>
                                  <input
                                    type="number"
                                    value={form.duration}
                                    onChange={(e) => updateField("duration", e.target.value)}
                                  />
                                </div>
                                <div className="mt-field">
                                  <label>Divisi</label>
                                  <select
                                    value={form.division}
                                    onChange={(e) => updateField("division", e.target.value)}
                                  >
                                    {divisionOptions.map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="mt-field">
                                  <label>Status</label>
                                  <select
                                    value={form.status}
                                    onChange={(e) => updateField("status", e.target.value)}
                                  >
                                    <option value="ACTIVE">Aktif</option>
                                    <option value="INACTIVE">Nonaktif</option>
                                  </select>
                                </div>
                                <div className="mt-field mt-field-actions">
                                  <button type="submit" className="button button-primary" disabled={saving}>
                                    {saving ? "Menyimpan…" : "Perbarui"}
                                  </button>
                                  <button type="button" className="button button-quiet" onClick={resetForm}>
                                    Batal
                                  </button>
                                </div>
                              </div>
                            </form>

                            {/* Pos Biaya */}
                            <section className="cost-panel">
                              <div className="cost-head">
                                <div>
                                  <span className="eyebrow">Rincian biaya layanan</span>
                                  <h4>Pos Biaya Tindakan</h4>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                  <div className="cost-summary">
                                    <span>Biaya {rupiah.format(rowCostTotal)}</span>
                                    <strong>Est. profit {rupiah.format(rowProfit)}</strong>
                                  </div>
                                  {!costEditMode && rowCosts.length > 0 && (
                                    <button
                                      type="button"
                                      className="button button-quiet button-sm"
                                      onClick={() => startCostEditMode(rowCosts)}
                                    >
                                      ✏ Edit Pos Biaya
                                    </button>
                                  )}
                                </div>
                              </div>

                              <form className="cost-form" onSubmit={handleAddCost}>
                                <div className="mt-field cost-catalog-field">
                                  <label>Pilih dari Master Biaya</label>
                                  <select value={selectedCostCatalogId} onChange={(e) => handleSelectCostCatalog(e.target.value)}>
                                    <option value="">-- Pilih Master Biaya / isi manual --</option>
                                    {rowCompatibleCostCatalog.map((c) => (
                                      <option key={c.id} value={c.id}>
                                        {c.name} - {serviceDivisionLabel(c.division)} - {c.cost_type === "PERCENTAGE" ? `${Number(c.amount || 0)}%` : rupiah.format(Number(c.amount || 0))}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="mt-field cost-name-field">
                                  <label>Nama Pos Biaya</label>
                                  <input
                                    type="text"
                                    value={costForm.name}
                                    onChange={(e) => updateCostField("name", e.target.value)}
                                    placeholder="Contoh: Anestesi lokal"
                                  />
                                </div>
                                <div className="mt-field">
                                  <label>Tipe</label>
                                  <select
                                    value={costForm.cost_type}
                                    onChange={(e) => updateCostField("cost_type", e.target.value)}
                                  >
                                    <option value="PER_TREATMENT">Per tindakan</option>
                                    <option value="PERCENTAGE">Persentase tarif</option>
                                    <option value="ALLOCATED">Alokasi operasional</option>
                                    <option value="MANUAL">Manual</option>
                                  </select>
                                </div>
                                <div className="mt-field">
                                  <label>Nominal</label>
                                  <input
                                    type="number"
                                    value={costForm.amount}
                                    onChange={(e) => updateCostField("amount", e.target.value)}
                                    placeholder="0"
                                  />
                                </div>
                                <div className="mt-field cost-note-field">
                                  <label>Catatan</label>
                                  <input
                                    type="text"
                                    value={costForm.notes}
                                    onChange={(e) => updateCostField("notes", e.target.value)}
                                    placeholder="Opsional"
                                  />
                                </div>
                                <div className="mt-field mt-field-actions">
                                  <button type="submit" className="button button-primary" disabled={savingCost}>
                                    {savingCost ? "Menyimpan..." : "+ Tambah Pos Biaya"}
                                  </button>
                                </div>
                              </form>

                              <table className="cost-table">
                                <thead>
                                  <tr>
                                    <th>Pos Biaya</th>
                                    <th>Tipe</th>
                                    <th>Nominal</th>
                                    <th>Catatan</th>
                                    {costEditMode && <th>Aksi</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {rowCosts.map((cost) => {
                                    const val = costEditValues[cost.id] || {};
                                    return (
                                      <tr key={cost.id} className={costEditMode ? "cost-row-editing" : ""}>
                                        <td>{cost.name}</td>
                                    <td>
                                      {costEditMode ? (
                                        <select
                                          className="cost-inline-input"
                                          value={val.cost_type || cost.cost_type || "PER_TREATMENT"}
                                          onChange={(e) => updateCostEditValue(cost.id, "cost_type", e.target.value)}
                                        >
                                          {COST_TYPE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                          ))}
                                        </select>
                                      ) : (COST_TYPE_OPTIONS.find((option) => option.value === cost.cost_type)?.label || cost.cost_type)}
                                    </td>
                                        <td>
                                          {costEditMode ? (
                                            <input
                                              type="number"
                                              className="cost-inline-input"
                                              value={val.amount ?? ""}
                                              onChange={(e) => updateCostEditValue(cost.id, "amount", e.target.value)}
                                            />
                                          ) : formatCostAmount(cost)}
                                        </td>
                                        <td>
                                          {costEditMode ? (
                                            <input
                                              type="text"
                                              className="cost-inline-input"
                                              value={val.notes ?? ""}
                                              onChange={(e) => updateCostEditValue(cost.id, "notes", e.target.value)}
                                              placeholder="Opsional"
                                            />
                                          ) : (cost.notes || "-")}
                                        </td>
                                        {costEditMode && (
                                          <td className="cost-action-cell">
                                            <button className="link-button link-danger" onClick={() => handleDeleteCost(cost)}>
                                              Hapus
                                            </button>
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                                  {!rowCosts.length ? (
                                    <tr>
                                      <td colSpan={costEditMode ? 5 : 4} className="muted">Belum ada pos biaya untuk tindakan ini.</td>
                                    </tr>
                                  ) : null}
                                </tbody>
                              </table>
                              {costEditMode && (
                                <div className="cost-edit-actions">
                                  <button
                                    type="button"
                                    className="button button-primary button-sm"
                                    disabled={savingAllCosts}
                                    onClick={() => handleSaveAllCosts(rowCosts)}
                                  >
                                    {savingAllCosts ? "Menyimpan..." : "Perbarui"}
                                  </button>
                                  <button type="button" className="button button-quiet button-sm" onClick={cancelCostEditMode}>
                                    Batal
                                  </button>
                                </div>
                              )}

                              <div className="cost-divider" />

                              {/* BHP Otomatis */}
                              <div className="cost-head">
                                <div>
                                  <span className="eyebrow">Stok otomatis</span>
                                  <h4>BHP Otomatis</h4>
                                </div>
                                <div className="cost-summary">
                                  <span>{rowAutoBhp.length} item BHP</span>
                                  <strong>Dipakai saat tindakan disimpan</strong>
                                </div>
                              </div>

                              <form className="cost-form" onSubmit={handleAddAutoBhp}>
                                <div className="mt-field cost-name-field">
                                  <label>Item BHP</label>
                                  <select
                                    value={autoBhpForm.bhp_item_id}
                                    onChange={(e) => {
                                      updateAutoBhpField("bhp_item_id", e.target.value);
                                      updateAutoBhpField("quantity", normalizeBhpQuantity(autoBhpForm.quantity, e.target.value));
                                    }}
                                  >
                                    <option value="">-- Pilih BHP otomatis --</option>
                                    {rowCompatibleBhp.map((item) => (
                                      <option
                                        key={item.id}
                                        value={item.id}
                                        disabled={rowAutoBhp.some((r) => r.bhp_item_id === item.id)}
                                      >
                                        {item.name}{item.unit ? ` (${item.unit})` : ""}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="mt-field">
                                  <label>Jumlah</label>
                                  <input
                                    type="number"
                                    min="1"
                                    step={quantityStepForBhp(autoBhpForm.bhp_item_id)}
                                    value={autoBhpForm.quantity}
                                    onChange={(e) => updateAutoBhpField("quantity", e.target.value)}
                                    onBlur={(e) => updateAutoBhpField("quantity", normalizeBhpQuantity(e.target.value, autoBhpForm.bhp_item_id))}
                                  />
                                </div>
                                <div className="mt-field cost-note-field">
                                  <label>Catatan</label>
                                  <input
                                    type="text"
                                    value={autoBhpForm.notes}
                                    onChange={(e) => updateAutoBhpField("notes", e.target.value)}
                                    placeholder="Contoh: sarung tangan 1 pasang"
                                  />
                                </div>
                                <div className="mt-field mt-field-actions">
                                  <button type="submit" className="button button-primary" disabled={savingAutoBhp}>
                                    {savingAutoBhp ? "Menyimpan..." : "+ Tambah BHP Otomatis"}
                                  </button>
                                </div>
                              </form>

                              <table className="cost-table">
                                <thead>
                                  <tr>
                                    <th>Item BHP</th>
                                    <th>Jumlah</th>
                                    <th>Catatan</th>
                                    <th>Aksi</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rowAutoBhp.map((r) => (
                                    <tr key={r.id}>
                                      <td>{bhpItemLabel(r.bhp_item_id)}</td>
                                      <td>{Number(r.quantity || 0)}</td>
                                      <td>{r.notes || "-"}</td>
                                      <td>
                                        <button className="link-button link-danger" onClick={() => handleDeleteAutoBhp(r)}>
                                          Hapus
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                  {!rowAutoBhp.length ? (
                                    <tr>
                                      <td colSpan={4} className="muted">Belum ada BHP otomatis untuk tindakan ini.</td>
                                    </tr>
                                  ) : null}
                                </tbody>
                              </table>
                            </section>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!visibleRows.length ? (
                <tr>
                  <td colSpan={10} className="muted">
                    Belum ada tindakan untuk poli ini.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
      </article>

      <style>{`
        .mt-form-row {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 14px;
        }
        .mt-field {
          flex: 1;
          min-width: 180px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .mt-field label {
          font-size: 12px;
          font-weight: 600;
          color: #6b6555;
        }
        .mt-field input,
        .mt-field select {
          padding: 9px 12px;
          border: 1px solid #e8e2d5;
          border-radius: 8px;
          font-size: 14px;
          background: #fff;
        }
        .mt-field-actions {
          flex-direction: row;
          align-items: flex-end;
          gap: 10px;
        }
        .cost-panel {
          margin-top: 22px;
          padding: 18px;
          border: 1px solid #baf4ce;
          border-radius: 8px;
          background: #fbfffc;
        }
        .cost-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 14px;
        }
        .cost-head h4 {
          margin: 4px 0 0;
          font-size: 18px;
        }
        .cost-summary {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
          color: #6b6555;
          font-size: 13px;
        }
        .cost-summary strong {
          color: #009944;
          font-size: 16px;
        }
        .cost-form {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: flex-end;
          margin-bottom: 16px;
        }
        .cost-catalog-field {
          flex: 1 0 100%;
        }
        .cost-name-field {
          flex: 1.4;
        }
        .cost-note-field {
          flex: 1.2;
        }
        .cost-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }
        .cost-table th {
          text-align: left;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #7a7364;
          padding: 9px 10px;
          border-bottom: 1px solid #e8e2d5;
          background: #f8f4ec;
        }
        .cost-table td {
          padding: 10px;
          border-bottom: 1px solid #f1ece0;
        }
        .cost-divider {
          height: 1px;
          background: #e8f5df;
          margin: 20px 0;
        }
        .table-panel .panel-head {
          padding: 1.25rem 1.25rem 0;
        }
        .service-tabs {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          padding: 0 1.25rem 14px;
        }
        .service-tab {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border: 1px solid #dbe8d5;
          background: #fffdf8;
          color: #25372f;
          border-radius: 999px;
          padding: 8px 13px;
          font-weight: 800;
          font-size: 13px;
          cursor: pointer;
        }
        .service-tab span {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 22px;
          height: 22px;
          padding: 0 7px;
          border-radius: 999px;
          background: #edf7ef;
          color: #547064;
          font-size: 12px;
        }
        .service-tab-active {
          background: #15803d;
          border-color: #15803d;
          color: #fff;
        }
        .service-tab-active span {
          background: rgba(255, 255, 255, 0.18);
          color: #fff;
        }
        .mt-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }
        .mt-table th {
          text-align: left;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: #7a7364;
          padding: 10px 12px;
          border-bottom: 1px solid #e8e2d5;
        }
        .mt-table td {
          padding: 12px;
          border-bottom: 1px solid #f1ece0;
        }
        .link-button {
          background: none;
          border: none;
          padding: 0;
          font-size: 13px;
          font-weight: 600;
          color: #2f6f4f;
          cursor: pointer;
        }
        .link-danger {
          color: #c0392b;
        }
        .link-active {
          color: #1a5c38;
          font-style: italic;
        }
        .mt-row-editing td {
          background: #f0faf3;
        }
        .edit-expansion-row td {
          padding: 0;
          background: #f5faf6;
          border-bottom: 2px solid #2d7a3a;
        }
        .edit-expansion-cell {
          padding: 0 !important;
        }
        .edit-expansion-inner {
          padding: 1.25rem 1.5rem 1.5rem;
          border-left: 4px solid #2d7a3a;
        }
        .expansion-form-header {
          font-size: 14px;
          color: #1a5c38;
          margin-bottom: 14px;
        }
        .edit-active-notice {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 13px;
          color: #6b6555;
          font-style: italic;
        }
        .mt-field input:disabled,
        .mt-field select:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .cost-row-editing td {
          background: #f0faf3;
        }
        .cost-inline-input {
          width: 100%;
          padding: 5px 8px;
          border: 1px solid #a3cfb4;
          border-radius: 6px;
          font-size: 13px;
          background: #fff;
        }
        .cost-action-cell {
          white-space: nowrap;
        }
        .cost-edit-actions {
          display: flex;
          gap: 8px;
          margin-top: 10px;
        }
        .button-sm {
          padding: 5px 14px;
          font-size: 13px;
        }
        @media (max-width: 760px) {
        }
      `}</style>
    </div>
  );
}
